import React, { useState, useEffect } from "react";
import { Button, Input, Tag, message } from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  templateAPIWithErrorHandling,
  templateHelpers,
} from "../../../api/feeTemplate/feeTemplate";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { confirmDelete } from "../../../components/common/confirmDialog";
import { actionColumnDef } from "../../../components/common/RowActions";
import ListToolbar, {
  selectVisibleColumns,
  useListToolbar,
} from "../../../components/common/ListToolbar";

const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = ["description", "status"];

export default function FeeTemplateTable() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    from: 0,
    to: 0,
    lastPage: 1,
  });
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    sort_by: "created_at",
    sort_order: "desc",
  });

  const {
    visibleFilters, setVisibleFilters,
    visibleColumns, setVisibleColumns,
  } = useListToolbar({
    defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
  });

  // Fetch data using the proper API
  const fetchTemplates = async (
    page = 1,
    perPage = 10,
    search = "",
    status = ""
  ) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        search: search.trim(),
        status,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
      };

      const response = await templateAPIWithErrorHandling.getTemplates(params);
      const { templates: templateData, pagination: paginationData } =
        templateHelpers.extractTemplateList(response);

      setTemplates(templateData);
      setPagination({
        current: paginationData.current,
        pageSize: paginationData.pageSize,
        total: paginationData.total,
        from: paginationData.from,
        to: paginationData.to,
        lastPage: paginationData.lastPage,
      });
    } catch (error) {
      message.error(error.message || "Không thể tải danh sách mẫu");
      console.error("Error fetching:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle table pagination change
  const handleTableChange = (paginationConfig, tableFilters, sorter) => {
    if (sorter && sorter.field) {
      const sortBy = sorter.field;
      const sortOrder = sorter.order === "descend" ? "desc" : "asc";
      setFilters((prev) => ({
        ...prev,
        sort_by: sortBy,
        sort_order: sortOrder,
      }));
    }

    let statusFilter = "";
    if (tableFilters && tableFilters.status && tableFilters.status.length > 0) {
      statusFilter = tableFilters.status[0] ? "active" : "inactive";
    }

    fetchTemplates(
      paginationConfig.current,
      paginationConfig.pageSize,
      searchText,
      statusFilter
    );
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchText(value);
    fetchTemplates(1, pagination.pageSize, value, filters.status);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchText("");
    fetchTemplates(1, pagination.pageSize, "", filters.status);
  };

  // Action handlers
  const handleEdit = (record) => {
    message.info(`Chỉnh sửa mẫu: ${record.name}`);
    navigate(`/templates/${record.id}`);
  };

  const handleDelete = async (record) => {
    try {
      const confirmed = await confirmDelete({ entity: "mẫu", name: record.name });
      if (!confirmed) return;

      await templateAPIWithErrorHandling.deleteTemplate(record.id);
      message.success("Xóa mẫu thành công");

      fetchTemplates(
        pagination.current,
        pagination.pageSize,
        searchText,
        filters.status
      );
    } catch (error) {
      message.error(error.message || "Không thể xóa mẫu!");
    }
  };

  const handleAdd = () => {
    message.info("Thêm mẫu mới");
    navigate("/store-template");
  };

  // ===== Định nghĩa cột =====
  const columnDefs = [
    {
      key: "index", label: "STT", fixed: true,
      column: {
        title: "STT", key: "index", width: 80,
        render: (_, __, index) =>
          (pagination.current - 1) * pagination.pageSize + index + 1,
      },
    },
    {
      key: "name", label: "Tên mẫu", fixed: true,
      column: { title: "Tên mẫu", dataIndex: "name", key: "name", width: 200, fixed: "left", sorter: true },
    },
    {
      key: "description", label: "Mô tả",
      column: {
        title: "Mô tả", dataIndex: "description", key: "description",
        ellipsis: true, width: 280,
        render: (text) => text || "-",
      },
    },
    {
      key: "status", label: "Trạng thái",
      column: {
        title: "Trạng thái", dataIndex: "status", key: "status", width: 130,
        render: (status) => (
          <Tag color={status ? "green" : "red"}>
            {status ? "Hoạt động" : "Không hoạt động"}
          </Tag>
        ),
        filters: [
          { text: "Hoạt động", value: true },
          { text: "Không hoạt động", value: false },
        ],
        filterMultiple: false,
      },
    },
    actionColumnDef(
      (record) => [
        { key: "edit", label: "Chỉnh sửa", icon: <EditOutlined />, tone: "edit", onClick: () => handleEdit(record) },
        { key: "delete", label: "Xóa", icon: <DeleteOutlined />, tone: "danger", onClick: () => handleDelete(record) },
      ],
      { width: 100 }
    ),
  ];

  // ===== Định nghĩa bộ lọc =====
  const filterDefs = [
    {
      key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <Input.Search
          placeholder="Tìm kiếm theo tên..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={handleSearch}
          onClear={handleClearSearch}
          enterButton={<SearchOutlined />}
          allowClear
        />
      ),
    },
  ];

  const columns = selectVisibleColumns(columnDefs, visibleColumns);
  const hasActiveFilter = !!searchText;

  return (
    <BrandedPageLayout
      title="Mẫu khoản thu"
      subtitle="Quản lý danh sách mẫu khoản thu."
      breadcrumbItems={[{ title: "Mẫu khoản thu" }, { title: "Danh sách" }]}
    >
      <ListToolbar
        filterDefs={filterDefs}
        visibleFilters={visibleFilters}
        onChangeVisibleFilters={setVisibleFilters}
        columnDefs={columnDefs}
        visibleColumns={visibleColumns}
        onChangeVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        hasActiveFilter={hasActiveFilter}
        onClearFilter={handleClearSearch}
        onReload={() => fetchTemplates(pagination.current, pagination.pageSize, searchText, filters.status)}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Thêm mẫu
          </Button>
        }
      />

      <ListTableCard
        columns={columns}
        dataSource={templates}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
        }}
        onChange={handleTableChange}
        emptyText="Không có dữ liệu"
      />
    </BrandedPageLayout>
  );
}
